import ToggleSwitch from "../atomic/ToggleSwitch";
import HeaderColumnData, {
  SubColumn,
} from "../../services/models/notificationsSettingsTableModels/headerColumnData.model";
import NotificationsSettingsTableData from "./NotificationsSettingsTableData.model";
import BodyColumnData from "./BodyColumnData.model";
import { useCallback } from "react";
import RequiredFieldTooltip from "../global/RequiredFieldTooltip";
interface HeaderColumnProps extends HeaderColumnData {
  columnNumber?: number;
  onSelectAll?: (id: string | number) => void;
  isEnabled?: any;
  isPersonalSettingsNotificationMedium?: boolean;
  readOnly?: boolean;
}
interface BodyColumnProps extends BodyColumnData {
  rowNumber: number;
  columnNumber: number;
  tableData: Array<BodyColumnData[]>;
  setTableData: (data: Array<BodyColumnData[]>) => void;
  readOnly?: boolean;
}
interface TableBodyProps {
  bodyData: Array<BodyColumnData[]>;
  setBodyData: (data: Array<BodyColumnData[]>) => void;
  readOnly?: boolean;
}

function HeaderSubColumn({
  name,
  uniqueKey,
  allSelected,
  key,
  onSelectAll,
  id,
  disabled,
  isEnabled,
  isPersonalSettingsNotificationMedium,
  readOnly,
}: SubColumn) {
  const referenceKey = uniqueKey ?? key;
  const filteredValuesForMediumTable = referenceKey
    ? isEnabled.filter(
        (item: object) =>
          !(item[`${referenceKey}_DISABLED` as keyof typeof item] && referenceKey)
      )
    : [];
  const isDisabled =
    disabled ||
    (referenceKey
      ? isEnabled?.every(
          (item: object) =>
            item[`${referenceKey}_DISABLED` as keyof typeof item] === true
        )
      : false);
  const allAgainstKeyEnabled = referenceKey
    ? filteredValuesForMediumTable?.every(
        (item: object) => item?.[`${referenceKey}` as keyof typeof item]
      )
    : false;
  const buttonText = allAgainstKeyEnabled ? "Disable All" : "Enable All";
  const memoizedShowPop = useCallback(() => {
    if (onSelectAll) {
      onSelectAll(referenceKey ?? id, !allAgainstKeyEnabled);
    }
  }, [id, onSelectAll, allAgainstKeyEnabled, referenceKey]);

  function showPop() {
    memoizedShowPop();
  }

  return (
    <th className="text-center px-6 py-4 pr-4 w-32">
      <p className="text-xs font-semibold text-royalBlue capitalize">{name}</p>
      {!isPersonalSettingsNotificationMedium && !readOnly && (
        <button
          disabled={isDisabled}
          onClick={showPop}
          className={`text-[10px] font-medium text-[#084E8B] ${
            !isDisabled && "hover:underline"
          } disabled:cursor-not-allowed mt-auto`}
        >
          {buttonText}
        </button>
      )}
    </th>
  );
}

function HeaderColumn({
  columnName,
  columnNumber,
  subColumns,
  onSelectAll,
  isEnabled,
  isPersonalSettingsNotificationMedium,
  readOnly,
}: HeaderColumnProps): JSX.Element {
  return (
    <th
      className={`border-r border-roundedInputBorder text-royalBlue text-left ${
        subColumns.length === 0 ? "align-top" : ""
      }`}
    >
      <p
        className={`text-left px-6 py-4 ${
          subColumns.length > 0 ? "border-b" : ""
        }`}
      >
        {" "}
        {columnName}
      </p>
      {subColumns && subColumns.length > 0 && (
        <table className="table-auto">
          {subColumns.map((column) => (
            <HeaderSubColumn
              onSelectAll={onSelectAll}
              isEnabled={isEnabled}
              isPersonalSettingsNotificationMedium={
                isPersonalSettingsNotificationMedium
              }
              {...column}
            />
          ))}
        </table>
      )}
    </th>
  );
}

function TableBodyColumn({
  data,
  rowNumber,
  columnNumber,
  tableData,
  setTableData,
  readOnly = false, // Default to false
}: BodyColumnProps & { readOnly?: boolean }): JSX.Element {
  function handleSwitchToggle(value: boolean, childIndex: number) {
    if (readOnly) return; // Disable toggle in read-only mode
    let temp = [...tableData];
    let foundRow = temp[rowNumber][columnNumber];
    if (Array.isArray(foundRow.data)) {
      foundRow.data[childIndex].value = value;
      setTableData(temp);
    }
  }

  if (Array.isArray(data))
    return (
      <td className="border-r border-roundedInputBorder py-3.5">
        <table className="table-auto w-full ">
          {data.map((column, childIndex) => (
            <td className="px-10" key={childIndex}>
              <RequiredFieldTooltip
                id={"Create Signature on File (SOF)"}
                required={column.disabled}
                tooltipText={column?.hoverText}
              >
                <ToggleSwitch
                  name={column.name}
                  disabled={column.disabled || readOnly} // Disable toggle in read-only mode
                  id="switch"
                  checked={
                    typeof column.value == "boolean" ? column.value : false
                  }
                  handleChange={(event) =>
                    handleSwitchToggle(event.target.checked, childIndex)
                  }
                />
              </RequiredFieldTooltip>
            </td>
          ))}
        </table>
      </td>
    );

  return <td className="border-r border-roundedInputBorder px-6 py-3.5">{data.name}</td>;
}

function TableBody({
  bodyData,
  setBodyData,
  readOnly = false,
}: Readonly<TableBodyProps>): JSX.Element {
  return (
    <tbody>
      {bodyData.length === 0 && (
        <tr>
          <td colSpan={2} className="text-center p-6">
            {" "}
            <p>No data found</p>
          </td>
        </tr>
      )}
      {bodyData.map((data, rowNumber) => (
        <tr key={rowNumber} className="border-b border-roundedInputBorder">
          {data.map((item, columnNumber) => (
            <TableBodyColumn
              {...item}
              rowNumber={rowNumber}
              tableData={bodyData}
              setTableData={setBodyData}
              columnNumber={columnNumber}
              key={Math.random()}
              readOnly={readOnly} // Pass the readOnly prop
            />
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function TableHeader({
  columns,
  onSelectAll,
  isEnabled,
  isPersonalSettingsNotificationMedium,
  readOnly = false,
}: Readonly<{
  columns: HeaderColumnProps[];
  onSelectAll: (id: string | number) => void;
  isEnabled: any;
  isPersonalSettingsNotificationMedium?: boolean;
  readOnly?: boolean;
}>): JSX.Element {
  return (
    <thead className="bg-sideNavGray border-b border-roundedInputBorder">
      <tr>
        {columns.map((column, index) => (
          <HeaderColumn
            key={column.columnName}
            columnNumber={index}
            onSelectAll={onSelectAll}
            isEnabled={isEnabled}
            isPersonalSettingsNotificationMedium={
              isPersonalSettingsNotificationMedium
            }
            readOnly={readOnly}
            {...column}
          />
        ))}
      </tr>
    </thead>
  );
}

function NotificationsSettingsTable({
  tableColumns,
  tableBody,
  setTableBody,
  onSelectAll,
  isEnabled,
  isPersonalSettingsNotificationMedium,
  readOnly = false,
}: Readonly<NotificationsSettingsTableData>): JSX.Element {
  return (
    <table className="table-auto border border-roundedInputBorder bg-white">
      <TableHeader
        columns={tableColumns}
        onSelectAll={onSelectAll}
        isEnabled={isEnabled}
        readOnly={readOnly}
        isPersonalSettingsNotificationMedium={
          isPersonalSettingsNotificationMedium
        }
      />
      <TableBody
        bodyData={tableBody}
        setBodyData={setTableBody}
        readOnly={readOnly}
      />
    </table>
  );
}

export default NotificationsSettingsTable;
